import cx from "classnames";

import { Optional } from "common/utils/types";
import { FetchError } from "common/fetch/types";

import Button, { ButtonProps } from "../buttons/Button";
import LoadingContainer from "../loading/LoadingContainer";
import FetchErrorContainer, { FetchErrorContainerProps } from "../errors/FetchErrorContainer";
import ErrorContainer, { ErrorContainerProps } from "../errors/ErrorContainer";
import CloseButton from "../buttons/CloseButton";
import Form from "../forms/Form";
import FetchErrorAlert, { FetchErrorAlertFieldErrors } from "../errors/FetchErrorAlert";
import Tooltip from "../content/Tooltip";

import styles from "../../styles/DrawerLayout.module.less";

type DrawerLayoutProps = {
  children: React.ReactNode;
};

const DrawerLayout = ({ children }: DrawerLayoutProps) => {
  return <div className={styles.layout}>{children}</div>;
};

type DrawerHeaderProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onClose: () => void;
};

const DrawerHeader = ({ title, description, onClose }: DrawerHeaderProps) => {
  return (
    <div className={styles.header}>
      <div className={styles.text}>
        {title && <h5 className={styles.title}>{title}</h5>}
        {description && <div className={styles.description}>{description}</div>}
      </div>
      <CloseButton className={styles.close} onClick={onClose} />
    </div>
  );
};

const DrawerContent = ({ children }: DrawerLayoutProps) => {
  return <div className={styles.content}>{children}</div>;
};

type DrawerActionsProps = {
  children: React.ReactNode;
  isSticky?: boolean;
};

const DrawerActions = ({ children, isSticky }: DrawerActionsProps) => {
  return <div className={cx(styles.actions, isSticky && styles.sticky)}>{children}</div>;
};

const DrawerActionsButton = (props: ButtonProps & { className?: never }) => {
  return <Button {...props} />;
};

const DrawerAlerts = ({ children }: DrawerLayoutProps) => {
  return <div className={styles.alerts}>{children}</div>;
};

type DrawerFormProps = {
  canSubmit: boolean;
  onSubmit: () => void;
  error?: Optional<FetchError>;
  fieldErrors?: FetchErrorAlertFieldErrors;
  isLoading?: boolean;
  submitText: string;
  children: React.ReactNode;
  hideSubmit?: boolean;
  size?: "m" | "l";
  submitTooltipContent?: string;
};

const DrawerForm = ({
  canSubmit,
  onSubmit,
  children,
  error,
  fieldErrors,
  isLoading,
  submitText,
  hideSubmit = false,
  size = "m",
  submitTooltipContent,
}: DrawerFormProps) => {
  const submitButton = (
    <DrawerActionsButton disabled={!canSubmit} type="submit" isLoading={isLoading}>
      {submitText}
    </DrawerActionsButton>
  );
  return (
    <Form className={cx(styles.form, styles[size])} onSubmit={onSubmit} canSubmit={canSubmit}>
      <DrawerContent>
        {error && <FetchErrorAlert error={error} fieldErrors={fieldErrors} />}
        <div className={styles.body}>{children}</div>
      </DrawerContent>
      <DrawerActions>
        {!hideSubmit &&
          (submitTooltipContent ? (
            <Tooltip
              isEnabled={!canSubmit}
              content={submitTooltipContent}
              className={styles.submitTooltip}
            >
              {submitButton}
            </Tooltip>
          ) : (
            submitButton
          ))}
      </DrawerActions>
    </Form>
  );
};

type DrawerFormGroupProps = {
  children: React.ReactNode;
  align?: "vertical" | "horizontal";
};

const DrawerFormGroup = ({ children, align = "vertical" }: DrawerFormGroupProps) => {
  return (
    <div className={`${styles.group} ${align === "horizontal" ? styles.row : ""}`}>{children}</div>
  );
};

const DrawerFormLabel = ({ children }: DrawerLayoutProps) => {
  return <div className={styles.label}>{children}</div>;
};

const DrawerFetchError = (props: FetchErrorContainerProps) => {
  return (
    <div className={styles.error}>
      <FetchErrorContainer {...props} />
    </div>
  );
};

const DrawerLoading = () => {
  return (
    <div className={styles.loading}>
      <LoadingContainer />
    </div>
  );
};

const DrawerError = (props: ErrorContainerProps) => {
  return (
    <div className={styles.error}>
      <ErrorContainer {...props} />
    </div>
  );
};

export default Object.assign(DrawerLayout, {
  Header: DrawerHeader,
  Content: DrawerContent,
  Form: Object.assign(DrawerForm, { Group: DrawerFormGroup, Label: DrawerFormLabel }),
  Alerts: Object.assign(DrawerAlerts),
  Actions: Object.assign(DrawerActions, { Button: DrawerActionsButton }),
  FetchError: DrawerFetchError,
  Loading: DrawerLoading,
  Error: DrawerError,
});
